import { uploadMediaFile, generateVideoPosterUrl } from "@/components/FileUpload";
import { trimVideoToClip, canTrimVideo } from "@/lib/videoTrim";

export const CLIP_BITRATES = { light: 1_100_000, standard: 2_500_000, hd: 5_000_000 };

// Fenêtre réellement découpée : [start, start + len], len ∈ [1, maxSec], bornée par la durée.
export const clipWindow = ({ videoDur, maxSec, needsTrim, trimStart, trimLen }) => {
  const start = needsTrim ? Math.round((trimStart || 0) * 10) / 10 : 0;
  const len = Math.max(1, Math.min(maxSec, trimLen || maxSec, (videoDur || maxSec) - start));
  return { start, len: Math.round(len * 10) / 10 };
};

// Résout l'URL du média à publier (partagé par la feuille PC, l'éditeur plein écran mobile et la file d'envoi) :
// image → envoi direct ; vidéo → découpe locale à l'extrait choisi (ou ré-encodage si trop lourde) puis envoi.
// Lot 613 — quand l'appareil ne sait pas découper (iPhone : pas de captureStream) ou que la découpe locale échoue,
// la vidéo entière part et `trim` {start, len} est renvoyé : l'appelant le transmet (trim_start / trim_duration)
// pour que le serveur coupe exactement l'extrait choisi — plus jamais « les 30 premières secondes ».
export const prepareStoryMediaUrl = async ({ media, config, t, videoDur, maxSec, storyVideoMaxMb, needsTrim, trimStart, trimLen, clipQuality, setProc, signal }) => {
  const progress = (phase) => (pct) => setProc?.({ phase, pct });
  if (media.media_type !== "video") {
    if (media.url) return { url: media.url, poster: media.poster_url || null, trim: null };
    setProc?.({ phase: "upload", pct: 0 });
    const data = await uploadMediaFile(media.file, { config, context: "story", onProgress: progress("upload"), signal });
    setProc?.(null);
    return { url: data.url, poster: null, trim: null };
  }
  if (media.url) return { url: media.url, poster: media.poster_url || null, trim: null, duration: null };
  const oversize = media.file && media.file.size > storyVideoMaxMb * 1024 * 1024;
  const tooLong = videoDur && videoDur > maxSec + 0.3;
  const wantTrim = !!needsTrim || !!tooLong;
  let fileToUpload = media.file;
  let trim = null;
  // Lot 618 — durée réelle du clip publié (la barre de progression du lecteur s'y cale : un clip MediaRecorder n'a pas de durée en en-tête).
  let duration = Number.isFinite(videoDur) && videoDur > 0 ? Math.round(videoDur * 10) / 10 : null;
  if (oversize || wantTrim) {
    const win = clipWindow({ videoDur, maxSec, needsTrim: wantTrim, trimStart, trimLen });
    duration = win.len;
    const unsupported = () => { throw { friendlyMessage: t("story.trim_unsupported", "Ton appareil ne peut pas découper cette vidéo. Choisis une vidéo plus courte ou plus légère.") }; };
    // Lot 616 — découpe locale iPhone via canvas (module story_trim_ios_local) ; sinon le serveur découpe.
    const allowCanvas = config?.settings?.features?.story_trim_ios_local !== false;
    if (!canTrimVideo({ allowCanvas })) {
      if (oversize) unsupported();
      trim = win;
    } else {
      setProc?.({ phase: "trim", pct: 0 });
      try {
        fileToUpload = await trimVideoToClip(media.file, win.start, win.len, {
          videoBitrate: CLIP_BITRATES[clipQuality] || CLIP_BITRATES.standard,
          allowCanvas,
          onProgress: (p) => setProc?.({ phase: "trim", pct: Math.round(p * 100) }),
        });
      } catch (e) {
        if (signal?.aborted) throw e;
        if (oversize) unsupported();
        fileToUpload = media.file; // secours : le serveur découpe
        trim = win;
      }
    }
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  }
  setProc?.({ phase: "upload", pct: 0 });
  // Envoi du clip + capture de sa première image (affiche) en parallèle — au début de l'extrait si le serveur découpe.
  const [data, poster] = await Promise.all([
    uploadMediaFile(fileToUpload, { config, context: "story", onProgress: progress("upload"), signal }),
    generateVideoPosterUrl(fileToUpload, trim ? trim.start : 0).catch(() => null),
  ]);
  setProc?.(null);
  return { url: data.url, poster: poster || null, trim, duration };
};

// Champs à ajouter au corps de la story : extrait à découper côté serveur (cf. prepareStoryMediaUrl → trim)
// + durée réelle du clip (lot 618, barre de progression du lecteur).
export const trimPayload = (m) => ({
  ...(m?.trim ? { trim_start: m.trim.start, trim_duration: m.trim.len } : {}),
  ...(m?.duration > 0 ? { video_duration: m.duration } : {}),
});

// Envoie le calque de dessin (PNG transparent) et renvoie son URL ; null si rien à envoyer.
export const uploadStoryDrawing = async (exportPng, config) => {
  const blob = await exportPng?.();
  if (!blob) return null;
  const file = new File([blob], `story-drawing-${Date.now()}.png`, { type: "image/png" });
  const data = await uploadMediaFile(file, { config, context: "story" });
  return data?.url || null;
};
